import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ShoppingCart, Menu, X, Phone, AlertTriangle, Package } from "lucide-react";
import { useCart } from "@/context/CartContext";
import LogoIcon from "@/components/LogoIcon";

const NavLink = ({ to, label }) => (
  <Link
    to={to}
    data-testid={`nav-link-${label.toLowerCase()}`}
    className="font-quicksand text-[13px] font-semibold uppercase tracking-wide text-[#202020]/80 hover:text-[#00b890] transition-colors"
  >
    {label}
  </Link>
);

export default function Header() {
  const { count, setOpen } = useCart();
  const [mobile, setMobile] = useState(false);
  const { pathname } = useLocation();

  return (
    <header className="sticky top-0 z-50 bg-white shadow-sm">

      {/* Main nav */}
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-2">
            <LogoIcon className="w-7 h-7" />
            <span className="font-quicksand text-xl font-extrabold tracking-tight">
              <span className="text-[#00d9a6]">Go</span>
              <span className="text-[#3d3d3d]">Protege</span>
              <span className="ml-2 text-xs font-semibold text-[#00d9a6] uppercase tracking-widest align-middle">Loja</span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-7">
            <NavLink to="/" label="Início" />
            <NavLink to="/loja?cat=rastreador" label="Rastreadores" />
            <NavLink to="/loja?cat=rele" label="Relés" />
            <NavLink to="/loja?cat=chicote" label="Chicotes" />
            <NavLink to="/loja?cat=acessorio" label="Acessórios" />
          </nav>

          <div className="flex items-center gap-3">
            <a
              href="https://goprotege.com"
              className="hidden lg:inline-flex font-quicksand text-xs font-semibold text-[#3d3d3d] hover:text-[#1BB0CE] transition-colors"
              data-testid="back-to-main-site"
            >
              ← Site principal
            </a>
            <Link
              to="/rastrear"
              className="relative p-2.5 rounded-full hover:bg-[#FAFAFA] transition-colors flex items-center justify-center"
              aria-label="Acompanhar pedido"
              title="Acompanhar pedido"
            >
              <Package className="w-5 h-5 text-[#3d3d3d]" strokeWidth={2} />
            </Link>
            <button
              data-testid="cart-open-button"
              onClick={() => setOpen(true)}
              className="relative p-2.5 rounded-full hover:bg-[#FAFAFA] transition-colors"
              aria-label="Abrir carrinho"
            >
              <ShoppingCart className="w-5 h-5 text-[#3d3d3d]" strokeWidth={2} />
              {count > 0 && (
                <span
                  data-testid="cart-count-badge"
                  className="absolute -top-1 -right-1 bg-gp-gradient text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center font-quicksand"
                >
                  {count}
                </span>
              )}
            </button>
            <button
              className="md:hidden p-2"
              onClick={() => setMobile((m) => !m)}
              data-testid="mobile-menu-toggle"
              aria-label="Menu"
            >
              {mobile ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {mobile && (
          <div className="md:hidden border-t border-[#f1f1f3] py-6 flex flex-col items-start gap-5 px-6">
            <Link
              to="/"
              onClick={() => setMobile(false)}
              className="font-quicksand text-sm font-semibold uppercase tracking-wide text-[#3d3d3d] hover:text-[#00d9a6] transition-colors"
            >
              Início
            </Link>
            <Link
              to="/loja?cat=rastreador"
              onClick={() => setMobile(false)}
              className="font-quicksand text-sm font-semibold uppercase tracking-wide text-[#3d3d3d] hover:text-[#00d9a6] transition-colors"
            >
              Rastreadores
            </Link>
            <Link
              to="/loja?cat=rele"
              onClick={() => setMobile(false)}
              className="font-quicksand text-sm font-semibold uppercase tracking-wide text-[#3d3d3d] hover:text-[#00d9a6] transition-colors"
            >
              Relés
            </Link>
            <Link
              to="/loja?cat=chicote"
              onClick={() => setMobile(false)}
              className="font-quicksand text-sm font-semibold uppercase tracking-wide text-[#3d3d3d] hover:text-[#00d9a6] transition-colors"
            >
              Chicotes
            </Link>
            <Link
              to="/loja?cat=acessorio"
              onClick={() => setMobile(false)}
              className="font-quicksand text-sm font-semibold uppercase tracking-wide text-[#3d3d3d] hover:text-[#00d9a6] transition-colors"
            >
              Acessórios
            </Link>
            <a
              href="https://goprotege.com"
              className="font-quicksand text-sm font-semibold text-[#666] hover:text-[#00d9a6] transition-colors pt-4 border-t border-[#f1f1f3] w-full max-w-[200px] self-center text-center"
            >
              ← Site principal
            </a>
          </div>
        )}
      </div>
    </header>
  );
}
